

























import { Component, Watch, mixins } from 'nuxt-property-decorator';
import ScoreIcon from '@/assets/svg/score.vue.svg';
import HeaderCommissionBalanceBadge from '@/components/global/Header/HeaderCommissionBalance/HeaderCommissionBalanceBadge.vue';
import CommissionOnboardingQuery from '@/apollo/queries/CommissionOnboarding.query';
import HeaderCommissionBalanceMixin from '@/components/global/Header/HeaderCommissionBalance/HeaderCommissionBalanceMixin';
import type { CountUp } from 'countup.js';

const ICON_ROTATE_ANGLE = 90;
const COUNTER_ANIMATION_DELAY_MS = 1000;
const COUNTER_ANIMATION_DURATION_S = 1.4;

@Component<HeaderCommissionBalance>({
  name: 'HeaderCommissionBalance',
  components: {
    ScoreIcon,
    HeaderCommissionBalanceBadge,
  },
  async mounted() {
    if (!document.hidden) {
      this.isTabFocused = true;
    }

    window.onfocus = () => {
      this.isTabFocused = true;
    };

    window.onblur = () => {
      this.isTabFocused = false;
    };

    this.subscribe();
  },
  beforeDestroy() {
    this.unsubscribe();
  },
})
export default class HeaderCommissionBalance extends mixins(HeaderCommissionBalanceMixin) {
  protected isTabFocused = false;
  protected scoreIconAngle = 0;
  protected animationCompleted = false;
  protected counter: CountUp | null = null;

  get commissionsDelta() {
    return this.commissionBalance?.delta ?? 0;
  }

  set commissionsDelta(newValue: number) {
    this.commissionBalance.delta = newValue;
  }

  get commissionsBalance() {
    return (this.commissionBalance?.currentBalance ?? 0) - this.commissionsDelta;
  }

  get shouldAnimate() {
    return this.commissionsDelta !== 0 && this.isTabFocused;
  }

  get isCurrentDeltaPositive() {
    return (this.commissionsDelta ?? 0) > 0;
  }

  get scoreIconStyle() {
    return { transform: `rotate(${this.scoreIconAngle}deg)` };
  }

  get counterStyles() {
    return { minWidth: `calc(${this.commissionsBalance.toString().length}ch + 2px)` };
  }

  @Watch('shouldAnimate')
  private shouldAnimateWatch(newVal: boolean): void {
    if (!newVal) return;

    this.animationCompleted = false;
    this.scoreIconAngle = this.isCurrentDeltaPositive
      ? this.scoreIconAngle + ICON_ROTATE_ANGLE
      : this.scoreIconAngle - ICON_ROTATE_ANGLE;
    setTimeout(() => {
      this.counter?.update(this.commissionsBalance + (this?.commissionsDelta ?? 0));
    }, COUNTER_ANIMATION_DELAY_MS);
  }

  @Watch('commissionsBalance', { immediate: true, deep: true })
  private commissionsBalanceWatch(newVal: number, oldVal: number): void {
    if (newVal !== oldVal) {
      this.animateCounter();
    }
  }

  async animateCounter() {
    const { CountUp } = await import('countup.js');
    const counterOptions = {
      startVal: this.commissionsBalance,
      duration: COUNTER_ANIMATION_DURATION_S,
      separator: ',',
    };
    this.counter = new CountUp(this.$refs.counter as HTMLElement, 0, counterOptions);
  }

  onAnimationCompleted() {
    if (!this.$auth.loggedIn) {
      return;
    }

    this.$api
      .post(`/api/war_room/user/commission_transactions/mark_as_shown`)
      .then(() => {
        this.commissionsDelta = 0;
        return this.$apollo.getClient().queryManager.watchQuery({ query: CommissionOnboardingQuery }).refetch();
      })
      .then(() => {
        this.animationCompleted = true;
      });
  }

  handleClick() {
    this.$analytics.logEvent('war-room__common__header-score__click');
    this.$router.push({ name: 'war-room-products' });
  }
}
